import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles, LayoutGrid } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { STAGES } from "@/lib/mh";
import { money } from "@/lib/onb";
import { STATUS_LABEL, STATUS_COLOR } from "@/lib/sky";

const Tot = ({ label, value, testId, accent }) => (
  <div data-testid={testId}>
    <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-muted-foreground">{label}</div>
    <div className="font-display text-3xl leading-none mt-1.5 tabular-nums" style={accent ? { color: accent } : undefined}>{value}</div>
  </div>
);

export const DeptHeader = ({ d, tot, back, search }) => (
  <header className="space-y-5 mh-rise" data-testid="dept-header">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <button onClick={back} data-testid="dept-back" className="flex items-center gap-2 h-9 pl-3 pr-4 rounded-full border border-white/10 text-xs text-muted-foreground hover:text-foreground hover:border-white/25 transition-colors">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to the sky <span className="font-mono text-[10px] opacity-60 hidden sm:inline">esc</span>
      </button>
      <div className="flex items-center gap-3 order-last sm:order-none w-full sm:w-auto">{search}</div>
      <Link to={`/departments/${d.slug}`} className="font-mono text-[11px] underline text-muted-foreground hover:text-foreground" data-testid="dept-controls-link">Department controls →</Link>
    </div>
    <div className="grid lg:grid-cols-[1.3fr_1fr] gap-6 items-end">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: d.hex }}>{d.tagline}</div>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-2" style={{ color: d.hex }} data-testid="dept-title">{d.name}</h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-3 max-w-2xl italic" data-testid="dept-manifesto">{d.manifesto}</p>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-4" data-testid="dept-totals">
        <Tot label="Active" value={`${tot.active}/${tot.total}`} testId="dept-tot-active" />
        <Tot label="Hours/mo" value={tot.tailored ? Math.round(tot.hours) : "—"} testId="dept-tot-hours" />
        <Tot label="$/mo" value={tot.tailored ? `$${money(tot.money)}` : "—"} accent="#D4FF3A" testId="dept-tot-money" />
        <Tot label="Needs input" value={tot.needs} accent={tot.needs ? "#FFB020" : undefined} testId="dept-tot-needs" />
        <Tot label="Current wave" value={tot.wave ? `W${tot.wave}` : "—"} testId="dept-tot-wave" />
      </div>
    </div>
  </header>
);

const Chip = ({ on, onClick, children, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`h-7 px-3 rounded-full border text-[11px] whitespace-nowrap transition-colors ${on ? "border-[#D4FF3A]/60 text-[#D4FF3A] bg-[#D4FF3A]/10" : "border-white/10 text-muted-foreground hover:text-foreground"}`}>{children}</button>
);

export const DeptFilters = ({ f, setF, tailored, mode, setMode, mobile }) => (
  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3" data-testid="dept-filters">
    <div className="flex flex-wrap items-center gap-1.5">
      {tailored && ["all", "1", "2", "3"].map((w) => <Chip key={w} on={f.wave === w} onClick={() => setF({ ...f, wave: w })} testId={`dept-wave-${w}`}>{w === "all" ? "All waves" : `Wave ${w}`}</Chip>)}
      <span className="w-px h-5 bg-white/10 mx-1" />
      {["all", "active", "running", "inactive"].map((s) => <Chip key={s} on={f.status === s} onClick={() => setF({ ...f, status: s })} testId={`dept-status-${s}`}>{s === "all" ? "All statuses" : STATUS_LABEL[s]}</Chip>)}
      <label className="flex items-center gap-2 ml-2 text-[11px] text-muted-foreground">
        <Switch checked={f.needsOnly} onCheckedChange={(v) => setF({ ...f, needsOnly: v })} data-testid="dept-needs-only" /> Only needs input
      </label>
    </div>
    {!mobile && (
      <div className="flex p-1 rounded-full bg-white/[0.04] border border-white/10 w-fit" data-testid="dept-mode-switch">
        {[["sky", "Starfield", Sparkles], ["cards", "Cards", LayoutGrid]].map(([k, t, I]) => (
          <button key={k} onClick={() => setMode(k)} data-testid={`dept-mode-${k}`}
            className={`h-8 px-3.5 rounded-full text-xs flex items-center gap-1.5 transition-colors ${mode === k ? "bg-white text-black" : "text-muted-foreground hover:text-foreground"}`}><I className="w-3.5 h-3.5" />{t}</button>
        ))}
      </div>
    )}
  </div>
);

export const RoleFacts = ({ a, st, outside }) => {
  const t = a.tailored, v = t?.value;
  return (
    <div className="space-y-1 text-xs">
      {v && t.enabled && <div data-testid="role-facts-value"><span className="text-[#D4FF3A]">${money(v.money_low)}–{money(v.money_high)}</span><span className="text-muted-foreground"> /mo · {v.hours_low}–{v.hours_high}h</span></div>}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-muted-foreground">
        <span style={{ color: STATUS_COLOR[st] }}>● {STATUS_LABEL[st]}</span>
        {t?.wave && t.enabled && <span>Wave {t.wave}</span>}
        <span>{a.autonomy}</span>
        {t && <span>Owner: <span className="text-foreground">{t.owner_email || "—"}</span></span>}
      </div>
      {outside > 0 && <div className="font-mono text-[10px] text-[#00E5FF]" data-testid="role-facts-outside">+{outside} linked role{outside > 1 ? "s" : ""} in other departments</div>}
    </div>
  );
};

export const RoleHover = ({ n, d, st, outside, hint }) => (
  <div className="absolute top-4 right-4 left-4 sm:left-auto sm:w-80 rounded-lg border bg-[#0A0A0F]/95 backdrop-blur-xl p-4 pointer-events-none mh-enter" style={{ borderColor: `${d.hex}55` }} data-testid="dept-role-hover">
    <div className="font-mono text-[10px] uppercase tracking-[0.24em]" style={{ color: d.hex }}>{d.name} · {n.stage}</div>
    <div className="font-display text-2xl mt-1">{n.name}</div>
    <p className="text-xs text-muted-foreground mt-1 mb-2">{n.one_liner}</p>
    <RoleFacts a={n} st={st} outside={outside} />
    <div className="flex justify-between mt-3 font-mono text-[10px]">
      <span className="text-muted-foreground"><span className="text-[#00E5FF]">— requires</span> · <span className="text-[#D4FF3A]">— enables</span></span>
      <span className="text-[#00E5FF]">{hint ? "tap again to open" : "click to open →"}</span>
    </div>
  </div>
);

const RoleCard = ({ a, p }) => {
  const st = p.status(a), lit = p.lit, dim = lit && !lit.has(a.slug);
  return (
    <button id={`role-card-${a.slug}`} onClick={() => p.onOpen(a.slug)} onMouseEnter={() => p.setHover(a)} onMouseLeave={() => p.setHover(null)}
      data-testid={`role-card-${a.slug}`} data-status={st}
      className={`w-full text-left rounded-lg border p-3.5 bg-white/[0.02] transition-[opacity,border-color,transform] duration-300 hover:-translate-y-0.5 ${st === "inactive" ? "opacity-50" : ""} ${dim ? "opacity-30" : ""}`}
      style={{ borderColor: p.sel === a.slug ? "#D4FF3A" : lit?.has(a.slug) ? `${p.hex}AA` : st === "needs" ? "#FFB02055" : "rgba(255,255,255,0.08)" }}>
      <div className="font-medium text-sm">{a.name}</div>
      <p className="text-xs text-muted-foreground mt-1 mb-2 line-clamp-2">{a.one_liner}</p>
      <RoleFacts a={a} st={st} outside={p.outside(a.slug)} />
    </button>
  );
};

export const StageColumns = ({ list, p }) => (
  <div className="flex md:grid md:grid-cols-4 gap-4 overflow-x-auto md:overflow-visible snap-x snap-mandatory pb-2 -mx-1 px-1" data-testid="dept-stage-columns">
    {STAGES.map((s, i) => {
      const items = list.filter((a) => a.stage === s);
      return (
        <div key={s} className="min-w-[85vw] md:min-w-0 snap-start space-y-3 mh-rise" style={{ animationDelay: `${i * 80}ms` }} data-testid={`dept-column-${s.toLowerCase()}`}>
          <div className="flex items-baseline justify-between border-b border-white/10 pb-2">
            <span className="font-display text-xl tracking-[0.18em] uppercase">{s}</span>
            <span className="font-mono text-[10px] text-muted-foreground">{items.length} jobs</span>
          </div>
          {items.map((a) => <RoleCard key={a.slug} a={a} p={p} />)}
          {!items.length && <div className="text-xs text-muted-foreground">No roles match.</div>}
        </div>
      );
    })}
  </div>
);
